import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLoadScript } from '@react-google-maps/api';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import to from 'await-to-js';
import { IconInfoCircleFilled } from '@tabler/icons-react';
import { ActionIcon, Popover } from '@mantine/core';
import { FormControl, MenuItem, Stack, Typography, Select, Button, Tooltip } from '@mui/material';
import authService from '../../api-authorization/AuthorizeService';
import StatCard from '../../components/StatCard';
import RequestOverlay from '../../components/RequestOverlay';
import {
  format,
  mergeClasses,
  getCoordinatesFromPolygonText
} from '../../../utils';
import styles from './RunDetails.module.css';
import Map from '../../components/Map';
import { selectRouteById } from '../../../store/runSlice';
import StatCardSingleSelect from '../../components/StatCard/StatCardSingleSelect';
import AlertDialog from '../../components/AlertDialog';
import toast from 'react-hot-toast';
import useSWR from 'swr';
import { Edit as EditIcon, NavigateBefore as NavigateBeforeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box } from '@mui/material';

// Fetcher for SWR with auth
const fetcher = async (url) => {
  const token = await authService.getAccessToken();
  const res = await fetch(
    `${process.env.REACT_APP_BASE_URL}${url}`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    }
  );
  if (!res.ok) throw new Error('Failed to fetch');
  return res.json();
};

const APPLY_NOZZLE_PROFILE_BUTTONS = {
  THIS_RUN: {
    id: 'thisRun', text: 'This run only'
  },
  ALL_RUNS: {
    id: 'allRuns', text: 'This run and all new runs'
  }
};

const APPLY_TARGET_RATE_BUTTONS = {
  THIS_RUN: {
    id: 'thisRun', text: 'This run only'
  },
  ALL_RUNS: {
    id: 'allRuns', text: 'This run and all new runs'
  }
};

function getColor(value) {
  switch (value) {
    case 'Completed':
      return 'text-green-200';
    case 'Syncing':
      return 'text-blue-900';
    case 'Running':
      return 'text-yellow-600';

    default:
      return 'text-white';
  }
}

const Title = ({ content }) => (
  <div className={styles.break}>
    <span style={{ position: 'relative' }}>
      {content}
      <ActionIcon
        style={{ pointerEvents: 'all' }}
        variant="transparent"
        size="xs"
        radius="xl"
        aria-label="Settings"
        className="statcardTooltip"
      >
        <IconInfoCircleFilled
          style={{
            width: '85%',
            height: '85%',
            color: '#FFF'
          }}
          stroke={1.5}
        />
      </ActionIcon>
    </span>{' '}
  </div>
);

function formatHeartbeatStalenessTime(time) {
  if (!time) return;
  const split = time.split(':');
  if (!split || split.length != 2) return;
  const hours = String(Math.floor(split[0] / 60));
  const minutes = String(split[0] % 60);
  const seconds = String(split[1]);
  return `${hours}h ${minutes}m ${seconds}s`;
}

// Add this mapping above your statData useMemo
const pwmModeDisplayTable = [
  { mode: "Spot Plain", display: "<spot>" },
  { mode: "Spot Spraying", display: "<spot>" },
  { mode: "Broadcast", display: "<cover>" },
  { mode: "Dual Rate", display: "<spot> and <cover>" },
  { mode: "SiC Plain", display: "<spot>" },
  { mode: "SiC Spot", display: "<spot>" },
  { mode: "SiC Cover", display: "<cover>" },
  { mode: "SiC Dual Rate", display: "<spot> and <cover>" }
];

// Helper function to get the display value
function getPwmDisplayValue(subLocationStats) {
  if (!subLocationStats || !subLocationStats.mostWeeditMode) return "-";
  const mode = subLocationStats?.mostWeeditMode;
  const spot = subLocationStats?.lastWeedItNozzleSpotPercent;
  const cover = subLocationStats?.lastWeedItNozzleCoverPercent;
  const row = pwmModeDisplayTable.find(
    (entry) => entry.mode.toLowerCase() === mode.toLowerCase()
  );
  if (!row) return "-";
  let value = row.display;
  const formatValue = (v) =>
    v !== undefined && v !== null && !isNaN(v)
      ? Number(v).toFixed(1).replace(/\.0$/, "")
      : "-";
  value = value.replace(/<spot>/g, formatValue(spot));
  value = value.replace(/<cover>/g, formatValue(cover));
  return value;
}

// Utility to convert array of objects to CSV string
function arrayToCsv(data) {
  if (!Array.isArray(data) || data.length === 0) return '';
  const keys = Object.keys(data[0]);
  const escape = (val) =>
    `"${String(val).replace(/"/g, '""').replace(/\n/g, ' ')}"`;
  const header = keys.join(',');
  const rows = data.map((row) =>
    keys.map((k) => escape(row[k] ?? '')).join(',')
  );
  return [header, ...rows].join('\r\n');
}

// Download helper
function downloadCsv(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

const DATA_MODE_OPTIONS = [
  { id: 'day', label: 'Entire Day' },
  { id: 'job', label: 'Job' }
];

const RunDetails = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, []);

  // Lock page scroll on desktop only — stats panel scrolls independently
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) return;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    const mainContainer = document.querySelector('.main__container');
    if (mainContainer) mainContainer.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (mainContainer) mainContainer.style.overflow = '';
    };
  }, []);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries: ['visualization']
  });

  const { runId, tractorId } = useParams();
  const dataModeStorageKey = `runDetails:dataMode:${runId}`;
  const selectedJobStorageKey = `runDetails:selectedJobId:${runId}`;
  const { data: nozzleProfiles } = useSWR(`/api/tractors/nozzle-profiles?tractorId=${tractorId}`);
  const { data: tractorData } = useSWR(`/api/tractors?tractorId=${tractorId}`, fetcher);
  const { data: imageData } = useSWR(`/api/imageCaptures?runId=${runId}`);
  const [subLocations, setSubLocations] = useState([]);
  const [saveNozzleProfileDialog, setSaveNozzleProfileDialog] = useState(false);
  const [saveTargetRateDialog, setSaveTargetRateDialog] = useState(false);
  const [selectedSubLocation, setSelectedSubLocation] = useState('');
  const [hasSetDefaultSubLocation, setHasSetDefaultSubLocation] = useState(false);
  const [pendingTargetApplicationRate, setPendingTargetApplicationRate] = useState(null);
  const [dataMode, setDataMode] = useState(() => localStorage.getItem(dataModeStorageKey) || 'day');
  const [selectedJobId, setSelectedJobId] = useState(() => localStorage.getItem(`runDetails:selectedJobId:${runId}`) || '');
  const [jobs, setJobs] = useState([]);
  const [dropdownWidth, setDropdownWidth] = useState(275);
  const toolbarRef = useRef(null);
  const newJobBtnRef = useRef(null);
  const [isMobileLayout, setIsMobileLayout] = useState(window.innerWidth <= 768);
  const asideTopRef = useRef(null);
  const [asideTopHeight, setAsideTopHeight] = useState(0);
  const mainHeaderRef = useRef(null);
  const [mainHeaderHeight, setMainHeaderHeight] = useState(0);

  // Run details data state
  const [runDetailsData, setRunDetailsData] = useState(null);
  const [runDetailsLoading, setRunDetailsLoading] = useState(true);
  const [runDetailsError, setRunDetailsError] = useState(null);
  const runStatusRef = useRef(null);
  const [selectedNozzleProfileId, setSelectedNozzleProfileId] = useState(null);

  // Dialog state
  const [editJobDialogOpen, setEditJobDialogOpen] = useState(false);
  const [editJobName, setEditJobName] = useState('');
  const [editJobId, setEditJobId] = useState(null);

  // Ref for the TextField
  const inputRef = useRef(null);

  // Highlight all text when dialog opens
  useEffect(() => {
    if (editJobDialogOpen && inputRef.current) {
      inputRef.current.select();
    }
  }, [editJobDialogOpen]);
  
  // Persist data mode and selected job to localStorage
  useEffect(() => {
    localStorage.setItem(dataModeStorageKey, dataMode);
  }, [dataMode, dataModeStorageKey]);

  useEffect(() => {
    setDataMode(localStorage.getItem(dataModeStorageKey) || 'day');
  }, [dataModeStorageKey]);

  useEffect(() => {
    if (selectedJobId) {
      localStorage.setItem(selectedJobStorageKey, selectedJobId);
    } else {
      localStorage.removeItem(selectedJobStorageKey);
    }
  }, [selectedJobId, selectedJobStorageKey]);

  useEffect(() => {
    setSelectedJobId(localStorage.getItem(selectedJobStorageKey) || '');
  }, [selectedJobStorageKey]);

  // Fetch run/job details based on mode
  useEffect(() => {
    let ignore = false;
    let intervalId;

    async function fetchDetails() {
      setRunDetailsError(null);
      try {
        const token = await authService.getAccessToken();
        let url;
        if (dataMode === 'job') {
          if (!selectedJobId) {
            setRunDetailsLoading(false);
            return;
          }
          url = `/api/jobs?runId=${runId}&jobId=${selectedJobId}`;
        } else {
          url = `/api/runs?runId=${runId}`;
        }
        console.log(url);
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}${url}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) {
          const err = new Error('Failed to fetch');
          err.status = res.status;
          throw err;
        }
        const json = await res.json();
        if (!ignore) setRunDetailsData(json);
      } catch (err) {
        if (!ignore) setRunDetailsError(err);
      } finally {
        if (!ignore) setRunDetailsLoading(false);
      }
    }

    fetchDetails();
    // Only poll for today's runs — past-date runs are never updated
    const isToday = (utcString) => {
      if (!utcString) return true; // unknown date, poll to be safe
      const d = new Date(utcString);
      const now = new Date();
      return d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();
    };
    intervalId = setInterval(async () => {
      if (!isToday(runStatusRef.current?.startTimeUtc)) {
        clearInterval(intervalId);
        return;
      }
      await fetchDetails();
    }, 10000);

    return () => {
      ignore = true;
      clearInterval(intervalId);
    };
  }, [dataMode, runId, selectedJobId]);

  // Keep runStatusRef in sync so interval callbacks avoid stale closure
  useEffect(() => {
    runStatusRef.current = runDetailsData?.run ?? null;
  }, [runDetailsData]);

  // Replace all references to `data` with `runDetailsData` and `isLoading`/`error` with `runDetailsLoading`/`runDetailsError`
  const data = runDetailsData;
  const isLoading = runDetailsLoading;
  const error = runDetailsError;

  // Fetch jobs when dataMode is 'job'
  useEffect(() => {
    if (dataMode === 'job' && runId) {
      const fetchJobs = async () => {
        try {
          const token = await authService.getAccessToken();
          const res = await fetch(
            `${process.env.REACT_APP_BASE_URL}/api/jobs/all?runId=${runId}`,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            }
          );
          if (!res.ok) throw new Error('Failed to fetch jobs');
          const jobsData = await res.json();
          setJobs(Array.isArray(jobsData) ? jobsData : []);
          if (Array.isArray(jobsData) && jobsData.length > 0) {
            const storedJobId = Number(localStorage.getItem(selectedJobStorageKey));
            const storedExists = storedJobId && jobsData.some(j => j.id === storedJobId);
            setSelectedJobId(storedExists ? storedJobId : jobsData[jobsData.length - 1].id);
          } else {
            setSelectedJobId('');
          }
        } catch {
          setJobs([]);
          setSelectedJobId('');
        }
      };
      fetchJobs();
    } else {
      setJobs([]);
      setSelectedJobId('');
    }
  }, [dataMode, runId]);

  // Fetch client data from /api/clients
  const { data: clientData } = useSWR('/api/clients', fetcher);

  // State for selected target application rate
  const [selectedTargetApplicationRate, setSelectedTargetApplicationRate] = useState('');

  // Handler for select change (show dialog)
  const handleChangeTargetApplicationRate = (selectedId) => {
    setPendingTargetApplicationRate(selectedId);
    setSaveTargetRateDialog(true);
  };

  // Handler for dialog close (cancel)
  const handleCloseSaveTargetRateDialog = () => {
    setPendingTargetApplicationRate(null);
    setSaveTargetRateDialog(false);
  };

  // Handler for dialog button click (save)
  const handleApplyTargetApplicationRate = async (buttonId) => {
    if (!pendingTargetApplicationRate) {
      setSaveTargetRateDialog(false);
      return;
    }
    const token = await authService.getAccessToken();
    const dataToSend = {
      runId,
      tractorId: Number(tractorId),
      targetApplicationRateGalAcre: pendingTargetApplicationRate,
      onlyThisRun: buttonId === APPLY_TARGET_RATE_BUTTONS.THIS_RUN.id
    };

    const requestOptions = {
      method: "PATCH",
      headers: !token
        ? {}
        : {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      body: JSON.stringify(dataToSend),
    };

    const fetchPromise = new Promise((resolve, reject) => {
      fetch(
        `${process.env.REACT_APP_BASE_URL}/api/runs/update-target-application-rate`,
        requestOptions
      )
        .then((response) => {
          if (!response.ok) {
            reject("Save failed");
            throw new Error("Save failed");
          }
          resolve("Save successful");
          setSelectedTargetApplicationRate(pendingTargetApplicationRate);
          setSaveTargetRateDialog(false);
        })
        .catch((error) => {
          reject("Save failed");
          setSaveTargetRateDialog(false);
          console.error("Save failed:", error);
        });
    });

    toast.promise(fetchPromise, {
      loading: "Saving...",
      success: "Save successful",
      error: "Save failed",
    });
  };

  useEffect(() => {
    setSelectedNozzleProfileId(data?.run?.tractorNozzleProfileId)
  }, [data?.run]);

  const filteredSubRuns = useMemo(() => {
    if (!data?.subRuns) return [];
    return data.subRuns;
  }, [data?.subRuns]);

  const validSubLocations = useMemo(
    () =>
      subLocations.filter((subLocation) =>
        filteredSubRuns.some((subRun) => subLocation.id === subRun.subLocationId)
      ),
    [subLocations, filteredSubRuns]
  );

  // Add this useMemo to get sorted blocks with gallons sprayed
  const sortedBlocks = useMemo(() => {
    if (!data?.subRuns || !validSubLocations.length) return [];
    // Map each block to include gallonsSprayed
    let blocks = validSubLocations.map((block) => {
      const subRun = data.subRuns.find((sr) => sr.subLocationId === block.id);
      return {
        ...block,
        gallonsSprayed: subRun?.totalPesticideSprayedGallons ?? 0
      };
    });

    // Always sort descending
    return blocks.sort((a, b) => b.gallonsSprayed - a.gallonsSprayed);
  }, [data, validSubLocations]);

  // Centralized helper for total gallons sprayed for "All Blocks"
  const totalGallonsSprayedAllBlocks = useMemo(() => {
    if (!filteredSubRuns.length) return 0;
    return filteredSubRuns.reduce(
      (sum, subRun) => sum + (subRun.totalPesticideSprayedGallons || 0),
      0
    );
  }, [filteredSubRuns]);

  useEffect(() => {
    // Only run after loading is complete and runDetailsData is available
    if (!isLoading && data && !hasSetDefaultSubLocation) {
      if (sortedBlocks.length > 0) {
        // If current selection is not a valid block, set to first block
        const isCurrentValid = sortedBlocks.some(
          b => String(b.id) === String(selectedSubLocation)
        );
        if (!isCurrentValid) {
          setSelectedSubLocation(String(sortedBlocks[0].id));
        }
        setHasSetDefaultSubLocation(true);
      } else if (sortedBlocks.length === 0) {
        // If no blocks, set to 'all'
        if (selectedSubLocation !== 'all') {
          setSelectedSubLocation('all');
        }
        setHasSetDefaultSubLocation(true);
      }
    }
    // Only run when these change
  }, [isLoading, data, sortedBlocks, hasSetDefaultSubLocation, selectedSubLocation]);

  // Reset default logic when runId changes
  useEffect(() => {
    setHasSetDefaultSubLocation(false);
    setSelectedSubLocation('');
  }, [runId]);

  const subLocationStats = useMemo(() => {
    if (!selectedSubLocation || !data) return;
    if (selectedSubLocation === 'all') {
      return data.run;
    }

    return data.subRuns.find(
      (i) => String(i.subLocationId) === String(selectedSubLocation)
    );
  }, [selectedSubLocation, data]);

  const polygons = useMemo(() => {
    if (!subLocations) return [];

    if (selectedSubLocation === 'all' || selectedSubLocation === 'only-blocks') {
      return filteredSubRuns
        .map(subRun => {
          const subLocation = subLocations.find(loc => loc.id === subRun.subLocationId);
          if (!subLocation) return null;
          return {
            name: subLocation.name,
            paths: getCoordinatesFromPolygonText(subLocation.boundaries),
            areaAcres: subLocation.areaAcres
          };
        })
        .filter(Boolean);
    } else {
      const foundBlock = subLocations.find(
        (i) => String(i.id) === String(selectedSubLocation)
      );
      if (!foundBlock) return [];
      return [
        {
          name: foundBlock.name,
          paths: getCoordinatesFromPolygonText(foundBlock?.boundaries),
          areaAcres: foundBlock.areaAcres
        }
      ];
    }
  }, [selectedSubLocation, subLocations]);

  const [activeMarker, setActiveMarker] = useState(null);
  const navigate = useNavigate();

  const runDataFromRedux = useSelector(selectRouteById(runId));

  const [markerType, setMarkerType] = React.useState(
    runDataFromRedux?.markerType || 'route-view'
  );

  const [isAiHeatmap, setIsAiHeatmap] = React.useState(
    runDataFromRedux?.isAiHeatmap || true
  );

  const markers = useMemo(() => {
    const result = [];
    for (let i = 0; i < imageData?.imageCaptures?.length; i++) {
      var imageCapture = imageData.imageCaptures[i];
      var images = imageCapture.image ? [imageCapture] : [];

      result.push({
        id: i + 1,
        images: images,
        eventDateUtc: imageCapture.eventDateUtc,
        position: {
          lat: imageCapture.lat,
          lng: imageCapture.long
        }
      });
    }
    return result;
  }, [imageData]);

  const solenoidOpenEvents = useMemo(() => {
    const result = [];

    for (let i = 0; i < data?.events?.length; i++) {
      var solenoidEvent = data.events[i];

      result.push({
        lat: solenoidEvent.lat,
        lng: solenoidEvent.long
      });
    }
    return result;
  }, [data]);

  const uniqueAiDiscoveries = useMemo(() => {
    const result = {};

    for (let i = 0; i < imageData?.imageCaptures?.length; i++) {
      var imageCapture = imageData.imageCaptures[i];
      if (imageCapture.aiDiscoveries) {
        for (let j = 0; j < imageCapture.aiDiscoveries?.length; j++) {
          let aiDiscovery = imageCapture.aiDiscoveries[j].trim();
          if (aiDiscovery in result) {
            result[aiDiscovery] += 1;
          } else {
            result[aiDiscovery] = 1;
          }
        }
      }
    }

    const sortedResponses = Object.fromEntries(
      Object.entries(result).sort(([, a], [, b]) => b - a)
    );

    return sortedResponses;
  }, [imageData]);

  const route = useMemo(() => {
    if (!data?.route || data.route.length === 0) return [];

    const result = [];

    for (let i = 0; i < data.route.length; i++) {
      result.push({
        lat: data.route[i].lat,
        lng: data.route[i].long,
        speedMph: data.route[i].speedMph,
        weedItOff: data.route[i].weedItPwmMode == '',
        weedItPressurePsi: data.route[i].weedItPressurePsi,
        weedItPwmMode: data.route[i].weedItPwmMode,
        weedItSensitivity: data.route[i].weedItSensitivity,
        weedItMargin: data.route[i].weedItMarginPreset,
        weedItSpeedMph: data.route[i].weedItSpeedMph,
        gpsQuality: data.route[i].gpsQuality,
        gpsNumSats: data.route[i].gpsNumSats,
        weedItStatus: data.route[i].weedItStatus,
        activeBoomNames: data.route[i].activeBoomNames,
        lteRSRQ: data.route[i].lteRSRQ,
        lteRSRP: data.route[i].lteRSRP,
        flowRateGpm: data.route[i].flowRateGpm,
        appRateGalPerAc: data.route[i].appRateGalPerAc,
        eventDateUtc: data.route[i].eventDateUtc
      });
    }

    return result;
  }, [data]);

  useEffect(() => {
    const fetchSubLocations = async () => {
      try {
        const token = await authService.getAccessToken();
        const headers = {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        };
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/sublocations/all`,
          headers
        );
        if (!res.ok) return;
        const newBlocks = await res.json();
        setSubLocations(newBlocks);
      } catch (error) { }
    };
    fetchSubLocations();
  }, []);

  const formattedTime = (time) => {
    if (!time) return '';
    const splitTime = time.split(':');
    return splitTime[0] + 'h' + ' ' + splitTime[1] + 'm';
  };

  const herbicideSavedPercent = useMemo(() => {
    const isBroadcast = subLocationStats?.mostWeeditMode?.toLowerCase().includes('broadcast');
    debugger;
    if (selectedSubLocation !== 'all') {
      const backendValue = subLocationStats?.herbicideSavedPercent;
      if (backendValue == null) return backendValue;
      // Backend always stores product-saved formula; reverse it for broadcast (target accuracy = 100 - productSaved)
      return isBroadcast ? (100 - backendValue).toFixed(1) : backendValue;
    }

    if (!filteredSubRuns.length) return 0;
    let totalActual = 0;
    let totalTheoretical = 0;
    filteredSubRuns.forEach(subRun => {
      const actual = Number(subRun.totalPesticideSprayedGallons);
      const area = Number(subRun.coverageAreaSprayedAcres);
      const rate = Number(subRun.applicationRateGalAcre);
      if (!isNaN(actual)) totalActual += actual;
      if (!isNaN(area) && !isNaN(rate)) totalTheoretical += area * rate;
    });
    if (totalTheoretical === 0) return 0;
    const result = isBroadcast
      ? 100 * (totalActual / totalTheoretical)
      : 100 * (1 - (totalActual / totalTheoretical));
    return result.toFixed(1);
  }, [selectedSubLocation, subLocationStats, filteredSubRuns]);

  const actualApplicationRate = useMemo(() => {
    if (selectedSubLocation !== 'all') {
      return subLocationStats?.actualApplicationRateGalAcre;
    }
    // Only use known blocks
    if (!filteredSubRuns.length) return 0;
    let totalCoverageAcres = 0;
    let totalSprayedGallons = 0;
    filteredSubRuns.forEach(subRun => {
      const sprayedGallons = Number(subRun.totalPesticideSprayedGallons);
      const coverageAcres = Number(subRun.coverageAreaSprayedAcres);
      if (!isNaN(coverageAcres)) totalCoverageAcres += coverageAcres;
      if (!isNaN(sprayedGallons)) totalSprayedGallons += sprayedGallons;
    });
    if (totalCoverageAcres === 0) return 0;
    return totalSprayedGallons / totalCoverageAcres;
  }, [selectedSubLocation, subLocationStats, filteredSubRuns]);

  // Calculate averages for Application Speed and Pressure when "All" is selected
  const avgSpeedMphAll = useMemo(() => {
    if (selectedSubLocation !== 'all') return undefined;
    if (!filteredSubRuns.length) return 0;
    const sum = filteredSubRuns.reduce((acc, subRun) => acc + (subRun.avgSpeedMph || 0), 0);
    return sum / filteredSubRuns.length;
  }, [selectedSubLocation, filteredSubRuns]);

  const avgPressurePsiAll = useMemo(() => {
    if (selectedSubLocation !== 'all') return undefined;
    if (!filteredSubRuns.length) return 0;
    const sum = filteredSubRuns.reduce((acc, subRun) => acc + (subRun.avgPressurePsi || 0), 0);
    return sum / filteredSubRuns.length;
  }, [selectedSubLocation, filteredSubRuns]);

  // Set default block selection only once per run, after data is loaded and blocks are available
  useEffect(() => {
    if (!isLoading && !hasSetDefaultSubLocation) {
      if (sortedBlocks.length > 0) {
        // If current selection is not a valid block, set to first block
        const isCurrentValid = sortedBlocks.some(
          b => String(b.id) === String(selectedSubLocation)
        );
        if (!isCurrentValid) {
          setSelectedSubLocation(sortedBlocks[0].id);
        }
        setHasSetDefaultSubLocation(true);
      } else if (sortedBlocks.length === 0) {
        // If no blocks, set to 'all'
        setSelectedSubLocation('all');
        setHasSetDefaultSubLocation(true);
      }
    }
  }, [isLoading, sortedBlocks, hasSetDefaultSubLocation, selectedSubLocation]);

  // Reset default logic when runId changes
  useEffect(() => {
    setHasSetDefaultSubLocation(false);
    setSelectedSubLocation('');
  }, [runId]);

  const statData = useMemo(() => {
    // Helper to get value from data.run or subLocationStats depending on selection
    const getStat = (field, sumFunc) => {
      if (selectedSubLocation === 'all') {
        // Prefer data.run if available, else fallback to sumFunc
        if (data?.run && data.run[field] !== undefined && data.run[field] !== null) {
          return data.run[field];
        }
        return typeof sumFunc === 'function' ? sumFunc() : '-';
      }
      return subLocationStats?.[field];
    };

    // Helper for formatting
    const formatNumber = (val, digits = 1) =>
      val === undefined || val === null || isNaN(val) ? '-' : Number(val).toFixed(digits);

    return [
      {
        title: <Title content={subLocationStats?.mostWeeditMode?.toLowerCase().includes('broadcast') ? 'Target Accuracy' : 'Product Saved'} />,
        value: formatNumber(herbicideSavedPercent),
        subtitle: 'percent',
        animate: true,
        tooltip: subLocationStats?.mostWeeditMode?.toLowerCase().includes('broadcast')
          ? <><strong>Application Accuracy</strong> represents the percent the <strong>Actual Rate</strong> applied is compared to the desired <strong>Target Rate</strong>.</>
          : <><strong>Product Saved</strong> represents the percent savings of the <strong>Actual Rate</strong> applied using spot spray technology verses the <strong>Target Rate</strong> that would have been applied if using traditional broadcast applications.</>

      },
      {
        title: <Title content="Total Area" />,
        value: formatNumber(getStat('totalAreaSprayedAcres', () =>
          filteredSubRuns.reduce(
            (sum, subRun) => sum + (subRun.totalAreaSprayedAcres || 0),
            0
          )
        )),
        subtitle: 'acres',
        animate: true,
        tooltip:
          <><strong>Total Area</strong> is calculated using the distance traveled times the row spacing assigned to the block boundary if set.  If the row spacing has not been set, it uses the distance traveled times the width from the Nozzle Profile selected.</>
      },
      {
        title: <Title content="Target Rate" />,
        value: formatNumber(getStat('applicationRateGalAcre')),
        subtitle: 'gal / acre',
        animate: true,
        tooltip:
          <><strong>Target Rate</strong> is the <strong>Target Application Rate</strong> set for the sprayer for the spray job.</>
      },
      {
        title: <Title content="Product Applied" />,
        value: formatNumber(getStat('totalPesticideSprayedGallons', () =>
          totalGallonsSprayedAllBlocks
        )),
        subtitle: 'gallons',
        animate: true,
        tooltip:
          <><strong>Product Applied</strong> is the total product applied reported by the flow meter.</>
      },
      {
        title: <Title content="Applied Area" />,
        value: formatNumber(getStat('coverageAreaSprayedAcres', () =>
          filteredSubRuns.reduce(
            (sum, subRun) => sum + (subRun.coverageAreaSprayedAcres || 0),
            0
          )
        )),
        subtitle: 'acres',
        animate: true,
        tooltip:
          <><strong>Applied Area</strong> is the distance traveled times the widths associated to the selected Nozzle Profile and the Active Booms enabled through the block.</>
      },
      {
        title: <Title content="Actual Rate" />,
        animate: true,
        tooltip: <><strong>Actual Rate</strong> is the rate calculated from the <strong>Product Applied</strong> divided by the <strong>Coverage Area</strong>.</>,
        value: formatNumber(getStat('actualApplicationRateGalAcre', () => {
          if (!filteredSubRuns.length) return 0;
          let totalCoverageAcres = 0;
          let totalSprayedGallons = 0;
          filteredSubRuns.forEach(subRun => {
            const sprayedGallons = Number(subRun.totalPesticideSprayedGallons);
            const coverageAcres = Number(subRun.coverageAreaSprayedAcres);
            if (!isNaN(coverageAcres)) totalCoverageAcres += coverageAcres;
            if (!isNaN(sprayedGallons)) totalSprayedGallons += sprayedGallons;
          });
          if (totalCoverageAcres === 0) return 0;
          return totalSprayedGallons / totalCoverageAcres;
        })),
        subtitle: 'gal / acre'
      },
      {
        title: <Title content="Speed" />,
        value: formatNumber(getStat('avgSpeedMph', () => {
          if (!filteredSubRuns.length) return 0;
          const sum = filteredSubRuns.reduce((acc, subRun) => acc + (subRun.avgSpeedMph || 0), 0);
          return sum / filteredSubRuns.length;
        })),
        subtitle: 'mph',
        animate: true,
        tooltip: <><strong>Speed</strong> is the average speed during the application.</>
      },
      {
        title: <Title content="Pressure" />,
        value: formatNumber(getStat('avgPressurePsi', () => {
          if (!filteredSubRuns.length) return 0;
          const sum = filteredSubRuns.reduce((acc, subRun) => acc + (subRun.avgPressurePsi || 0), 0);
          return sum / filteredSubRuns.length;
        })),
        subtitle: 'psi',
        animate: true,
        tooltip: <><strong>Pressure</strong> is the average pressure during the application.</>
      },
      {
        title: <Title content="Block Area" />,
        value: formatNumber(
          selectedSubLocation === 'all'
            ? validSubLocations.reduce((sum, block) => sum + (block.areaAcres || 0), 0)
            : (() => {
              const found = validSubLocations.find(
                block => String(block.id) === String(selectedSubLocation)
              );
              // If block is "Unmapped" or areaAcres is undefined/null/0, show '-'
              if (!found || found.name === 'Unmapped' || found.areaAcres === undefined || found.areaAcres === null || found.areaAcres === 0) {
                return '-';
              }
              return found.areaAcres;
            })()
        ),
        subtitle: 'acres',
        animate: true,
        tooltip: <><strong>Block Area</strong> is the sum of area of the selected block(s) that the vehicle has traveled through. If a specific block is selected in the dropdown, then that blocks area is displayed. If the block has not been mapped, no area will be shown here.</>
      },
      {
        title: <Title content="Sensitivity" />,
        tooltip: (() => {
          // weeditSensitivities: { [subLocationId]: { [sensitivity]: percent, ... }, ... }
          const sensitivities =
            data?.weeditSensitivities || {};

          // Helper to format a dictionary of { [sensitivity]: percent }
          const formatSensitivities = (sensDict) => {
            if (!sensDict || typeof sensDict !== 'object' || Object.keys(sensDict).length === 0) {
              return <div>No sensitivity data available.</div>;
            }
            return (
              <div>
                {Object.entries(sensDict).map(([sens, percent]) => (
                  <div key={sens}>
                    <strong>{sens}:</strong> {(percent * 100).toFixed(1)}%
                  </div>
                ))}
              </div>
            );
          };

          let sensitivitiesContent;

          if (selectedSubLocation === 'all') {
            // Sum all sublocation sensitivities
            const sum = {};
            let subLocCount = 0;
            for (const subLocId in sensitivities) {
              const sensDict = sensitivities[subLocId];
              for (const sens in sensDict) {
                sum[sens] = (sum[sens] || 0) + sensDict[sens];
              }
              subLocCount++;
            }
            // Average the percentages across sublocations
            for (const sens in sum) {
              sum[sens] = sum[sens] / (subLocCount || 1);
            }
            sensitivitiesContent = (
              <>
                <div>
                  <strong>Sensitivity</strong> displays the last sensitivity reported from the WEED-IT console.  1 is the most sensitive and 5 is generally the least sensitive.  
                </div>
                <div style={{ marginTop: 8, fontWeight: 500 }}>
                  Sensitivity breakdown (average across all blocks):
                </div>
                {formatSensitivities(sum)}
              </>
            );
          } else {
            // Show for selected sublocation
            const subLocSens = sensitivities[selectedSubLocation];
            sensitivitiesContent = (
              <>
                <div>
                  <strong>Sensitivity</strong> displays the last sensitivity reported from the WEED-IT console.  1 is the most sensitive and 5 is generally the least sensitive.  
                </div>
                <div style={{ marginTop: 8, fontWeight: 500 }}>
                  Sensitivity breakdown across selected blocks:
                </div>
                {formatSensitivities(subLocSens)}
              </>
            );
          }

          return sensitivitiesContent;
        })(),
        animate: true,
        value: (() => {
          const val = getStat('mostWeeditSensitivity', () => '-');
          return (val === 0 || val === '0' || val === null || val === undefined) ? '-' : val;
        })(),
        statSubtitleClassName: styles.statSubtitle,
        subtitle: `val`
      },
      {
        title: <Title content="Spray Mode" />,
        animate: true,
        tooltip: (() => {
          // weeditModes: { [subLocationId]: { [mode]: percent, ... }, ... }
          const modes = data?.weeditModes || {};

          // Helper to format a dictionary of { [mode]: percent }
          const formatModes = (modeDict) => {
            if (!modeDict || typeof modeDict !== 'object' || Object.keys(modeDict).length === 0) {
              return <div>No mode data available.</div>;
            }
            return (
              <div>
                {Object.entries(modeDict).map(([mode, percent]) => (
                  <div key={mode}>
                    <strong>{mode}:</strong> {(percent * 100).toFixed(1)}%
                  </div>
                ))}
              </div>
            );
          };

          let modesContent;

          if (selectedSubLocation === 'all') {
            // Sum all sublocation modes
            const sum = {};
            let subLocCount = 0;
            for (const subLocId in modes) {
              const modeDict = modes[subLocId];
              for (const mode in modeDict) {
                sum[mode] = (sum[mode] || 0) + modeDict[mode];
              }
              subLocCount++;
            }
            // Average the percentages across sublocations
            for (const mode in sum) {
              sum[mode] = sum[mode] / (subLocCount || 1);
            }
            modesContent = (
              <>
                <div>
                  <strong>Spray Mode</strong> displays the last mode reported by the WEED-IT console.
                </div>
                <div style={{ marginTop: 8, fontWeight: 500 }}>
                  Mode breakdown (average across all blocks):
                </div>
                {formatModes(sum)}
              </>
            );
          } else {
            // Show for selected sublocation
            const subLocModes = modes[selectedSubLocation];
            modesContent = (
              <>
                <div>
                  <strong>Spray Mode</strong> displays the last mode reported by the WEED-IT console.
                </div>
                <div style={{ marginTop: 8, fontWeight: 500 }}>
                  Mode breakdown used in selected block:
                </div>
                {formatModes(subLocModes)}
              </>
            );
          }

          return modesContent;
        })(),
        value: (() => {
          const val = getStat('mostWeeditMode', () => '-');
          return (!val || val === '') ? '-' : val;
        })(),
        valueClassName: styles.smallerWordWrap,
        subtitle: `mode`,
        statSubtitleClassName: styles.statSubtitleClassName,
        className: styles.smallPadding
      },
      {
        title: <Title content="PWM Setting" />,
        tooltip: <><strong>PWM Setting</strong> is the last PWM value reported by the WEED-IT console.</>,
        animate: true,
        value: getPwmDisplayValue(selectedSubLocation === 'all' ? data?.run : subLocationStats),
        statSubtitleClassName: styles.statSubtitle,
        subtitle: `percent`
      },
      {
        title: <Title content="Margin" />,
        tooltip: <><strong>Margin</strong> is the last margin reported by the WEED-IT console.</>,
        animate: true,
        value: (() => {
          const val = getStat('lastWeedItMarginPreset', () => '-');
          return (val === 0 || val === '0' || val === null || val === undefined) ? '-' : val;
        })(),
        statSubtitleClassName: styles.statSubtitle,
        subtitle: `val`
      }
    ];
  }, [
    actualApplicationRate,
    herbicideSavedPercent,
    subLocationStats,
    filteredSubRuns,
    validSubLocations,
    selectedSubLocation,
    data?.subRuns,
    avgSpeedMphAll,
    avgPressurePsiAll,
    data?.run
  ]);

  const handleSaveNozzleProfileDialog = () => setSaveNozzleProfileDialog((prev) => !prev);

  const handleCloseSaveActiveNozzleProfileDialog = () => {
    setSelectedNozzleProfileId(data?.run?.tractorNozzleProfileId);
    handleSaveNozzleProfileDialog();
  };

  const handleChangeNozzleProfile = (selectedId) => {
    setSelectedNozzleProfileId((prevSelectedBooms) => {
      const selectedObject = nozzleProfiles?.find(option => selectedId === option.id);
      return selectedObject?.id;
    });
    handleSaveNozzleProfileDialog();
  };

  const handleApplyActiveNozzleProfile = async (buttonId) => {
    const token = await authService.getAccessToken();

    const data = {
      runId,
      tractorId: Number(tractorId),
      tractorNozzleProfileId: selectedNozzleProfileId,
      onlyThisRun: buttonId === APPLY_NOZZLE_PROFILE_BUTTONS.THIS_RUN.id
    };

    const requestOptions = {
      method: "PATCH",
      headers: !token
        ? {}
        : {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      body: JSON.stringify(data),
    };

    const fetchPromise = new Promise((resolve, reject) => {
      fetch(
        `${process.env.REACT_APP_BASE_URL}/api/runs/update-tractor-nozzle-profile`,
        requestOptions
      )
        .then((response) => {
          if (!response.ok) {
            reject("Save failed");
            throw new Error("Save failed");
          }

          resolve("Save successful");
          setInitialValue(text);
          handleSaveNozzleProfileDialog();
        })
        .catch((error) => {
          reject("Save failed");
          handleSaveNozzleProfileDialog();
          console.error("Save failed:", error);
        });
    });

    toast.promise(fetchPromise, {
      loading: "Saving...",
      success: "Save successful",
      error: "Save failed",
    });
  };

  // Handler for Raw Data button
  const handleDownloadRawData = async () => {
    try {
      const token = await authService.getAccessToken();
      const headers = !token
        ? {}
        : { Authorization: `Bearer ${token}` };
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/runs/heartbeats?runId=${runId}`,
        { headers }
      );
      if (!response.ok) throw new Error('Failed to fetch raw data');
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        toast.error('No raw data available for this run.');
        return;
      }
      const csv = arrayToCsv(data);

      // Get sprayer name and date
      const sprayerName = data[0]?.tractorName || "Sprayer";
      const eventDate = data[0]?.eventDateUtc
        ? new Date(data[0].eventDateUtc)
        : new Date();
      const formattedDate = `${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}-${eventDate.getFullYear()}`;

      // Sanitize sprayer name for filename
      const safeSprayerName = sprayerName.replace(/[^a-zA-Z0-9-_]/g, "_");

      downloadCsv(csv, `${safeSprayerName}-${formattedDate}.csv`);
    } catch (err) {
      console.log(err);
      toast.error('Failed to download raw data');
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobileLayout(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track toolbar height (aside) and title/nav header height (main) to align stat cards with map
  useEffect(() => {
    const el = asideTopRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setAsideTopHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = mainHeaderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setMainHeaderHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Dynamically shrink dropdowns when they crowd the Start New Job button
  useEffect(() => {
    if (isMobileLayout) return;
    const toolbar = toolbarRef.current;
    if (!toolbar) return;
    const observer = new ResizeObserver(() => {
      const totalWidth = toolbar.offsetWidth;
      const btnWidth = newJobBtnRef.current ? newJobBtnRef.current.offsetWidth + 32 : 160;
      const numDropdowns = toolbar.querySelectorAll('[data-dropdown]').length || 3;
      const available = totalWidth - btnWidth;
      const perDropdown = Math.floor(available / numDropdowns) - 16;
      setDropdownWidth(Math.min(275, Math.max(80, perDropdown)));
    });
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [isMobileLayout]);

  const handleStartNewJob = async () => {
    try {
      const token = await authService.getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/jobs`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ runId })
        }
      );
      if (!response.ok) throw new Error('Failed to start new job');
      const result = await response.json(); // <-- Get jobId from response
      toast.success('New job started');

      if (dataMode !== 'job') {
        setDataMode('job');
      }

      if (runId) {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/jobs/all?runId=${runId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        if (!res.ok) throw new Error('Failed to fetch jobs');
        const jobsData = await res.json();
        setJobs(Array.isArray(jobsData) ? jobsData : []);
        if (Array.isArray(jobsData) && jobsData.length > 0) {
          // Use the returned jobId to select the new job
          setSelectedJobId(result.jobId);
        } else {
          setSelectedJobId('');
        }
      }
    } catch (err) {
      toast.error('Failed to start new job');
    }
  };

  // Handler to open dialog
  const handleOpenEditJobDialog = (job) => {
    setEditJobId(job.id);
    setEditJobName(job.displayName || '');
    setEditJobDialogOpen(true);
  };

  // Handler to close dialog
  const handleCloseEditJobDialog = () => {
    setEditJobDialogOpen(false);
    setEditJobId(null);
    setEditJobName('');
  };

  // Handler to submit rename
  const handleEditJobSubmit = async () => {
    try {
      const token = await authService.getAccessToken();
      // Find the job object and update its displayName
      const job = jobs.find(j => j.id === editJobId);
      if (!job) throw new Error('Job not found');
      const updatedJob = { ...job, name: editJobName };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/jobs`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify(updatedJob)
        }
      );
      if (!response.ok) throw new Error('Failed to update job');
      toast.success('Job renamed');
      handleCloseEditJobDialog();
      // Refresh jobs list
      if (dataMode === 'job' && runId) {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/jobs/all?runId=${runId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        if (!res.ok) throw new Error('Failed to fetch jobs');
        const jobsData = await res.json();
        setJobs(Array.isArray(jobsData) ? jobsData : []);
        if (Array.isArray(jobsData) && jobsData.length > 0) {
          setSelectedJobId(jobsData[0].id);
        } else {
          setSelectedJobId('');
        }
      }
    } catch (err) {
      toast.error('Failed to rename job');
    }
  };

  useEffect(() => {
    if (subLocationStats?.applicationRateGalAcre !== undefined && subLocationStats?.applicationRateGalAcre !== null) {
      setSelectedTargetApplicationRate(subLocationStats.applicationRateGalAcre);
    }
  }, [subLocationStats?.applicationRateGalAcre]);

  // derive current sprayer segment dynamically from the URL
  const location = useLocation();
  const sprayerSegment = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('precision-spray');
    return idx >= 0 && parts.length > idx + 1 ? parts[idx + 1] : '';
  }, [location.pathname]);

  // ADD: handlers to navigate to previous/next run using NextPreviousRuns payload
  const handleNavigateToPreviousRun = () => {
    const nextPrev = data?.nextPreviousRuns;
    if (!nextPrev?.previousRunId || !nextPrev?.previousDisplayId) return;

    navigate(
      `/precision-spray/${sprayerSegment}/${nextPrev.previousTractorId}/run-details/${nextPrev.previousRunId}/${nextPrev.previousDisplayId}`
    );
  };

  const handleNavigateToNextRun = () => {
    const nextPrev = data?.nextPreviousRuns;
    if (!nextPrev?.nextRunId || !nextPrev?.nextDisplayId) return;

    navigate(
      `/precision-spray/${sprayerSegment}/${nextPrev.nextTractorId}/run-details/${nextPrev.nextRunId}/${nextPrev.nextDisplayId}`
    );
  };


  return (
    <>


      <div className={styles.container}>
        <RequestOverlay
          isLoading={isLoading}
          error={error}
          errorCode={error?.status}
          id="loading-overlay"
          errorTitle="Your data couldn't be loaded."
          errorSubtitle="Please contact an administrator."
        />

        {isMobileLayout && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 1.5, pb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 0, textAlign: 'center' }}>
              {data?.run?.startTimeUtc ? format(new Date(data?.run?.startTimeUtc), 'M/d/yy') : ''} -{' '}
              {selectedSubLocation === 'all'
                ? 'All blocks selected'
                : `Block ${subLocations.find((block) => String(block.id) === String(selectedSubLocation))?.name}`}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
              <Tooltip title={data?.nextPreviousRuns?.previousTractorStartTime ? format(new Date(data.nextPreviousRuns.previousTractorStartTime), 'M/d/yy') : ''} arrow>
                <span>
                  <Button variant="outlined" startIcon={<NavigateBeforeIcon />} onClick={handleNavigateToPreviousRun} disabled={!data?.nextPreviousRuns?.previousRunId} sx={{ minWidth: 120 }}>
                    Previous
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={data?.nextPreviousRuns?.nextTractorStartTime ? format(new Date(data.nextPreviousRuns.nextTractorStartTime), 'M/d/yy') : ''} arrow>
                <span>
                  <Button variant="outlined" endIcon={<NavigateNextIcon />} onClick={handleNavigateToNextRun} disabled={!data?.nextPreviousRuns?.nextRunId} sx={{ minWidth: 120 }}>
                    Next
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}

        <section style={{ width: '100%', zIndex: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, paddingTop: '20px' }}>
          <div
            className={mergeClasses(
              'grid  grid-cols-[600px_1fr] gap-[4px]',
              styles.gridContainer,
              styles.gridFill
            )}
            style={{ flex: 1, minHeight: 0, display: 'grid', gap: 0 }}
          >
            <main style={{ zIndex: 10, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', minHeight: 0, paddingLeft: '30px', paddingRight: '16px' }}>
              {/* Fixed title/nav header — height tracked to offset scrollable stats */}
              {!isMobileLayout && (
                <div ref={mainHeaderRef}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, marginBottom: '8px', paddingTop: '8px' }}>
                    <Typography variant="h5" sx={{ fontWeight: 500, mb: 0, textAlign: 'center' }}>
                      {data?.run?.startTimeUtc ? format(new Date(data?.run?.startTimeUtc), 'M/d/yy') : ''} -{' '}
                      {selectedSubLocation === 'all'
                        ? 'All blocks selected'
                        : `Block ${subLocations.find((block) => String(block.id) === String(selectedSubLocation))?.name}`}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, width: '100%', marginTop: '8px' }}>
                      <Tooltip title={data?.nextPreviousRuns?.previousTractorStartTime ? format(new Date(data.nextPreviousRuns.previousTractorStartTime), 'M/d/yy') : ''} arrow>
                        <span>
                          <Button variant="outlined" startIcon={<NavigateBeforeIcon />} onClick={handleNavigateToPreviousRun} disabled={!data?.nextPreviousRuns?.previousRunId} sx={{ minWidth: 120 }}>
                            Previous
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={data?.nextPreviousRuns?.nextTractorStartTime ? format(new Date(data.nextPreviousRuns.nextTractorStartTime), 'M/d/yy') : ''} arrow>
                        <span>
                          <Button variant="outlined" endIcon={<NavigateNextIcon />} onClick={handleNavigateToNextRun} disabled={!data?.nextPreviousRuns?.nextRunId} sx={{ minWidth: 120 }}>
                            Next
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </div>
              )}
              {/* Scrollable stats body — paddingTop aligns first stat card with map top */}
              <div
                style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingTop: isMobileLayout ? 8 : Math.max(8, asideTopHeight + 8 - mainHeaderHeight), paddingBottom: '4px' }}
              >
              <div
                className={
                  styles.responsiveStatCard +
                  ' run-details-container'
                }
                style={{ zIndex: 2 }}
              >
                <div>
                  <StatCard data={statData} />

                  <StatCard
                    className="mt-8"
                    textAreaClassName={styles.textArea}
                    gridClassName={
                      subLocationStats?.runStatus != 'Running'
                        ? ''
                        : styles.gridTwoFifty
                    }
                    data={[
                      {
                        title: <Title content="Duration" />,
                        animate: false,
                        tooltip: <><strong>Duration</strong> is the total amount of time the sprayer spent moving.</>,
                        value: formattedTime(subLocationStats?.duration),
                        valueClassName: styles.smallerWordWrap,
                        subtitle: 'time',
                        statSubtitleClassName: styles.statSubtitleClassName,
                        className: styles.smallPadding
                      },
                      {
                        title: (
                          <div className={styles.break}>
                            <span style={{ position: 'relative' }}>
                              Status
                              <ActionIcon
                                style={{ pointerEvents: 'all' }}
                                variant="transparent"
                                size="xs"
                                radius="xl"
                                aria-label="Settings"
                                className="statcardTooltip"
                              >
                                <IconInfoCircleFilled
                                  style={{
                                    width: '85%',
                                    height: '85%',
                                    color: '#FFF'
                                  }}
                                  stroke={1.5}
                                />
                              </ActionIcon>
                            </span>{' '}
                          </div>
                        ),
                        animate: false,
                        tooltip:<>
                          <strong>Status</strong> is the status of the unit.<br />
                          <strong>Running</strong> - Denotes the vehicle is actively moving and/ or product is being applied.<br />
                          <strong>Complete</strong> - All data has been uploaded and the vehicle is no longer applying product.</>,
                        valueClassName:
                          getColor(data?.run?.runStatus) +
                          ' runDetailStatusStyle',
                        value: data?.run?.runStatus || '',
                        className: styles.smallPadding
                      },

                      {
                        title: <Title content="Delay" />,
                        animate: false,
                        tooltip:
                          <><strong>Delay</strong> is the amount of time that has gone by since the last reported time data was received from the vehicle.</>,
                        value: formatHeartbeatStalenessTime(
                          data?.heartbeatStalenessTime
                        ),
                        valueClassName: styles.smallerWordWrap,
                        statSubtitleClassName: styles.statSubtitleClassName,
                        className: styles.smallPadding
                      }
                    ]}
                  />

                  {Array.isArray(clientData?.targetApplicationRates) && clientData.targetApplicationRates.length > 1 && (
                    <StatCardSingleSelect
                      options={clientData.targetApplicationRates.map(rate => ({
                        id: rate,
                        name: `${rate} gal/acre`
                      }))}
                      selectedOption={selectedTargetApplicationRate}
                      handleChange={handleChangeTargetApplicationRate}
                      title={'Target Application Rate'}
                      tooltip={<><strong>Target Application Rate</strong> is the desired rate for the application.</>}
                    />
                  )}

                  <StatCardSingleSelect
                    options={nozzleProfiles}
                    selectedOption={selectedNozzleProfileId}
                    handleChange={handleChangeNozzleProfile}
                    title={'Nozzle Profile'}
                    tooltip={<><strong>Nozzle Profile</strong> is the nozzle profile that is to be used for the application.  The nozzle profile is used to calculate the width of the application swath.  If nozzles are capped to allow for changing row widths or banding options, the nozzle profile needs to be changed to match the application.</>}
                  />

                  <StatCard
                    className="mt-8"
                    gridClassName={styles.statCardContainerTwo}
                    textarea={{
                      note: data?.run?.note,
                      runId
                    }}
                    textAreaClassName={styles.textArea}

                  />
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12, marginBottom: 16 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleDownloadRawData}
                      sx={{ fontWeight: 500 }}
                    >
                      Download Raw Data
                    </Button>
                  </div>
                </div>
              </div>
              </div>
            </main>

            <aside style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', paddingLeft: 16, paddingRight: 35, paddingBottom: 20 }}>
              {/* Toolbar height tracked to align first stat card with map top */}
              <div ref={asideTopRef}>
              {/* Toolbar: desktop = single row, mobile = grid */}
              {isMobileLayout ? (
                /* MOBILE LAYOUT */
                <div ref={toolbarRef} style={{ marginTop: 8, width: '100%', boxSizing: 'border-box' }}>
                  {/* Dropdowns: always 2 columns so 3 items = 2 top + 1 bottom, 4 items = 2x2 */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: '12px',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}>
                    {/* Data Mode */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Typography variant="subtitle2"><strong>Data Mode</strong></Typography>
                      <Select value={dataMode} onChange={(e) => setDataMode(e.target.value)} size="small" fullWidth>
                        {DATA_MODE_OPTIONS.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </div>
                    {/* Jobs (only when data mode is job) */}
                    {dataMode === 'job' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Typography variant="subtitle2"><strong>Jobs</strong></Typography>
                        <Select value={selectedJobId} onChange={(e) => setSelectedJobId(e.target.value)} size="small" fullWidth disabled={jobs.length === 0}
                          renderValue={selected => { const job = jobs.find(j => j.id === selected); return job ? job.displayName || `Job ${job.id}` : ''; }}>
                          {jobs.length > 0 ? jobs.map(job => (
                            <MenuItem key={job.id} value={job.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span>{job.displayName || `Job ${job.id}`}</span>
                              <ActionIcon size="sm" color="primary" sx={{ ml: 1 }} onClick={e => { e.stopPropagation(); handleOpenEditJobDialog(job); }}>
                                <EditIcon fontSize="small" />
                              </ActionIcon>
                            </MenuItem>
                          )) : <MenuItem value="" disabled>No jobs found</MenuItem>}
                        </Select>
                      </div>
                    )}
                    {/* Block */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Typography variant="subtitle2"><strong>Block</strong></Typography>
                        <Popover width={320} shadow="md" withArrow withinPortal>
                          <Popover.Target>
                            <ActionIcon variant="transparent" size="xs" radius="xl" style={{ color: 'black', cursor: 'pointer' }}>
                              <IconInfoCircleFilled style={{ width: '85%', height: '85%' }} stroke={1.5} />
                            </ActionIcon>
                          </Popover.Target>
                          <Popover.Dropdown>Individual block flow counts may not sum up to the 'All' value if the sprayer drove outside of known geo fenced areas.</Popover.Dropdown>
                        </Popover>
                      </div>
                      <Select value={selectedSubLocation} onChange={(e) => setSelectedSubLocation(e.target.value)} size="small" fullWidth>
                        {sortedBlocks.map((block) => (
                          <MenuItem value={block.id} key={block.id}>{block.name} - {Number(block.gallonsSprayed).toFixed(1)} gal</MenuItem>
                        ))}
                        <MenuItem value="all" sx={{ fontWeight: 'bold' }}>All Blocks - {Number(totalGallonsSprayedAllBlocks).toFixed(1)} gal</MenuItem>
                      </Select>
                    </div>
                    {/* Overlay */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Typography variant="subtitle2"><strong>Overlay</strong></Typography>
                      <Select value={markerType} onChange={(e) => setMarkerType(e.target.value)} size="small" fullWidth>
                        <MenuItem value="route-view">Route</MenuItem>
                        <MenuItem value="active-boom-overlay">Active Booms</MenuItem>
                        <MenuItem value="application-rate-gal-ac-overlay">Application Rate (gal/ac)</MenuItem>
                        <MenuItem value="flow-rate-overlay">Flow Rate (gal/min)</MenuItem>
                        <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center'}}>-------- GPS Data --------</MenuItem>
                        <MenuItem value="gps-speed-overlay">Speed</MenuItem>
                        <MenuItem value="gps-quality-overlay">Quality</MenuItem>
                        <MenuItem value="gps-satellites-overlay"># Satellites</MenuItem>
                        <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center' }}>-------- Weed-It Data --------</MenuItem>
                        <MenuItem value="weed-it-speed-overlay">Speed</MenuItem>
                        <MenuItem value="weed-it-pressure-overlay">Pressure</MenuItem>
                        <MenuItem value="weed-it-spray-mode-overlay">Spray Mode</MenuItem>
                        <MenuItem value="weed-it-sensitivity-overlay">Sensitivity</MenuItem>
                        <MenuItem value="weed-it-margin-overlay">Margin</MenuItem>
                        <MenuItem value="weed-it-status-overlay">Status</MenuItem>
                        <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center' }}>-------- Signal Data --------</MenuItem>
                        <MenuItem value="lte-rsrp-overlay">LTE Strength</MenuItem>
                        <MenuItem value="lte-rsrq-overlay">LTE Quality</MenuItem>
                      </Select>
                    </div>
                    {/* AI Results */}
                    {uniqueAiDiscoveries && Object.keys(uniqueAiDiscoveries).includes(markerType) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Typography variant="subtitle2"><strong>AI Results</strong></Typography>
                        <Select value={isAiHeatmap} onChange={(e) => setIsAiHeatmap(e.target.value)} size="small" fullWidth>
                          <MenuItem value={false}>Markers</MenuItem>
                          <MenuItem value={true}>Heatmap</MenuItem>
                        </Select>
                      </div>
                    )}
                  </div>
                  {/* Start New Job button below grid */}
                  <Button ref={newJobBtnRef} variant="contained" color="primary" onClick={handleStartNewJob}
                    sx={{ mt: 1.5, width: '100%', fontWeight: 500 }}>
                    Start New Job
                  </Button>
                </div>
              ) : (
                /* DESKTOP LAYOUT */
                <Stack
                  ref={toolbarRef}
                  direction="row"
                  sx={{
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    position: 'relative',
                    marginTop: '8px',
                    gap: '8px',
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                {/* Data Mode Dropdown */}
                <FormControl data-dropdown sx={{ flexShrink: 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}>
                      <strong>Data Mode</strong>
                    </Typography>
                    <Select
                      value={dataMode}
                      onChange={(e) => setDataMode(e.target.value)}
                      style={{ width: dropdownWidth }}
                    >
                      {DATA_MODE_OPTIONS.map(opt => (
                        <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </div>
                </FormControl>
                {/* Jobs Dropdown (only if Data Mode is 'job') */}
                {dataMode === 'job' && (
                  <FormControl data-dropdown sx={{ flexShrink: 0 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}>
                        <strong>Jobs</strong>
                      </Typography>
                      <Select
                        value={selectedJobId}
                        onChange={(e) => setSelectedJobId(e.target.value)}
                        style={{ width: dropdownWidth }}
                        disabled={jobs.length === 0}
                        renderValue={selected => {
                          const job = jobs.find(j => j.id === selected);
                          return job ? job.displayName || `Job ${job.id}` : '';
                        }}
                      >
                        {jobs.length > 0 ? (
                          jobs.map(job => (
                            <MenuItem key={job.id} value={job.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span>
                                {job.displayName ? job.displayName : `Job ${job.id}`}
                              </span>
                              <ActionIcon
                                size="sm"
                                color="primary"
                                sx={{ ml: 1 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleOpenEditJobDialog(job);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </ActionIcon>
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="" disabled>No jobs found</MenuItem>
                        )}
                      </Select>
                    </div>
                  </FormControl>
                )}
                {/* Select Block */}
                <FormControl data-dropdown sx={{ flexShrink: 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', marginRight: 1 }}>
                        <strong>Block</strong>
                      </Typography>
                      <Popover width={320} shadow="md" withArrow withinPortal>
                        <Popover.Target>
                          <ActionIcon
                            variant="transparent"
                            size="xs"
                            radius="xl"
                            aria-label="Block Info"
                            style={{ marginLeft: 4, color: 'black', cursor: 'pointer' }}
                          >
                            <IconInfoCircleFilled
                              style={{ width: '85%', height: '85%' }}
                              stroke={1.5}
                            />
                          </ActionIcon>
                        </Popover.Target>
                        <Popover.Dropdown>
                          Individual block flow counts may not sum up to the 'All' value if the sprayer drove outside of known geo fenced areas.
                        </Popover.Dropdown>
                      </Popover>
                    </div>
                    <Select
                      value={selectedSubLocation}
                      onChange={(e) => setSelectedSubLocation(e.target.value)}
                      style={{ width: dropdownWidth }}
                    >
                      {sortedBlocks.map((block) => (
                        <MenuItem value={block.id} key={block.id}>
                          {block.name} - {Number(block.gallonsSprayed).toFixed(1)} gal
                        </MenuItem>
                      ))}
                      <MenuItem value="all" sx={{ fontWeight: 'bold' }}>
                        All Blocks - {Number(totalGallonsSprayedAllBlocks).toFixed(1)} gal
                      </MenuItem>
                    </Select>
                  </div>
                </FormControl>
                {/* Select Overlay */}
                <FormControl data-dropdown sx={{ flexShrink: 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6">
                        <strong>Overlay</strong>
                      </Typography>
                    </div>
                    <Select
                      value={markerType}
                      onChange={(e) => setMarkerType(e.target.value)}
                      style={{ width: dropdownWidth }}
                    >
                      <MenuItem value="route-view">Route</MenuItem>
                      <MenuItem value="active-boom-overlay">Active Booms</MenuItem>
                      <MenuItem value="application-rate-gal-ac-overlay">Application Rate (gal/ac)</MenuItem>
                      <MenuItem value="flow-rate-overlay">Flow Rate (gal/min)</MenuItem>
                      <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center'}}>
                        -------- GPS Data --------
                      </MenuItem>
                      <MenuItem value="gps-speed-overlay">Speed</MenuItem>
                      <MenuItem value="gps-quality-overlay">Quality</MenuItem>
                      <MenuItem value="gps-satellites-overlay"># Satellites</MenuItem>                      
                      <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center' }}>
                        -------- Weed-It Data --------
                      </MenuItem>
                      <MenuItem value="weed-it-speed-overlay">Speed</MenuItem>
                      <MenuItem value="weed-it-pressure-overlay">Pressure</MenuItem>
                      <MenuItem value="weed-it-spray-mode-overlay">Spray Mode</MenuItem>
                      <MenuItem value="weed-it-sensitivity-overlay">Sensitivity</MenuItem>
                      <MenuItem value="weed-it-margin-overlay">Margin</MenuItem>
                      <MenuItem value="weed-it-status-overlay">Status</MenuItem>
                      <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center' }}>
                        -------- Signal Data --------
                      </MenuItem>
                      <MenuItem value="lte-rsrp-overlay">LTE Strength</MenuItem>
                      <MenuItem value="lte-rsrq-overlay">LTE Quality</MenuItem>
                    </Select>
                  </div>
                </FormControl>
                {uniqueAiDiscoveries &&
                  Object.keys(uniqueAiDiscoveries).includes(markerType) && (
                    <FormControl data-dropdown sx={{ flexShrink: 0 }}>
                      <Typography variant="h6">
                        <strong>AI Results</strong>
                      </Typography>
                      <Select
                        value={isAiHeatmap}
                        onChange={(e) => setIsAiHeatmap(e.target.value)}
                        style={{ width: dropdownWidth }}
                      >
                        <MenuItem value={false}>Markers</MenuItem>
                        <MenuItem value={true}>Heatmap</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', height: '100%', flexShrink: 0 }}>
                  <Button
                    ref={newJobBtnRef}
                    variant="contained"
                    color="primary"
                    onClick={handleStartNewJob}
                    sx={{ marginTop: 4, marginLeft: 'auto', fontWeight: 500, whiteSpace: 'nowrap' }}
                  >
                    Start New Job
                  </Button>
                </div>
              </Stack>
              )} {/* end desktop/mobile ternary */}
              </div> {/* end asideTopRef */}

              {isLoaded ? (
                <>
                  <div style={{ height: isMobileLayout ? '112.5vw' : 'calc(100vh - 325px)', marginTop: 8, marginBottom: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: '15px' }}>
                  <Map
                    id={runId}
                    polygons={polygons}
                    baseUrl={imageData?.baseImagePath}
                    markers={markers}
                    solenoidOpenEvents={solenoidOpenEvents}
                    route={route}
                    markerType={markerType}
                    activeMarker={activeMarker}
                    setActiveMarker={setActiveMarker}
                    setMarkerType={setMarkerType}
                    isRunOver={data?.run?.runStatus == 'Completed'}
                    isAiHeatmap={isAiHeatmap}
                    mapStyle={{ height: '100%' }}
                    tractor={tractorData?.tractor}
                  />
                  </div>
                </>
              ) : null}
            </aside>
          </div>
        </section>
      </div>
      <AlertDialog
        open={saveNozzleProfileDialog}
        buttonClick={handleApplyActiveNozzleProfile}
        onClose={handleCloseSaveActiveNozzleProfileDialog}
        title={'Update the nozzle profile for only this run, or for this run and all new runs?'}
        buttons={[
          { id: APPLY_NOZZLE_PROFILE_BUTTONS.THIS_RUN.id, text: APPLY_NOZZLE_PROFILE_BUTTONS.THIS_RUN.text },
          { id: APPLY_NOZZLE_PROFILE_BUTTONS.ALL_RUNS.id, text: APPLY_NOZZLE_PROFILE_BUTTONS.ALL_RUNS.text }
        ]}
      />
      <AlertDialog
        open={saveTargetRateDialog}
        buttonClick={handleApplyTargetApplicationRate}
        onClose={handleCloseSaveTargetRateDialog}
        title={'Update the target application rate for only this run, or for this run and all new runs?'}
        buttons={[
          { id: APPLY_TARGET_RATE_BUTTONS.THIS_RUN.id, text: APPLY_TARGET_RATE_BUTTONS.THIS_RUN.text },
          { id: APPLY_TARGET_RATE_BUTTONS.ALL_RUNS.id, text: APPLY_TARGET_RATE_BUTTONS.ALL_RUNS.text }
        ]}
      />
      <Dialog open={editJobDialogOpen} onClose={handleCloseEditJobDialog}>
        <DialogTitle>Rename Job</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Job Name"
            type="text"
            fullWidth
            value={editJobName}
            onChange={e => setEditJobName(e.target.value)}
            inputRef={inputRef}
            onFocus={e => e.target.select()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditJobDialog}>Cancel</Button>
          <Button onClick={handleEditJobSubmit} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RunDetails;